import { memo, useCallback, useMemo } from 'react';
import { Slider as MUISlider } from '@mui/material';
import {
  AB3, B1, S3, S4,
} from '../../../theme';
import { TYPOGRAPHY_CAPTION } from '../../Typography';
import { ISliderProps } from '../index';
import { mergeSX } from '../../../services/helpers';

interface IBuildSXParams {
  color: string;
  withMargin: boolean;
  disabled: boolean;
}

const buildSX = ({ color, withMargin, disabled }: IBuildSXParams) => ({
  '& .MuiSlider-rail': {
    color: S3,
    opacity: 1,
    maxHeight: 2,
  },
  '& .MuiSlider-track': {
    maxHeight: 0,
    color: disabled ? S3 : AB3,
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: 'transparent',
    color: B1,
    ...TYPOGRAPHY_CAPTION,
  },
  '& .MuiSlider-markLabel': {
    top: 36,
    color: S4,
    ...TYPOGRAPHY_CAPTION,
  },
  '& .MuiSlider-mark': {
    width: 0,
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    color: AB3,
    '&.Mui-disabled': {
      color: S3,
    },
    '&[data-index="1"]': {
      '& .MuiSlider-valueLabelCircle': {
        marginLeft: withMargin ? 3 : 0,
      },
    },
    '&[data-index="0"]': {
      '& .MuiSlider-valueLabelCircle': {
        marginRight: withMargin ? 3 : 0,
      },
    },
    '&:hover': {
      boxShadow: `0px 0px 0px 8px ${color}70`,
    },
    '&.Mui-focusVisible': {
      height: 24,
      width: 24,
      boxShadow: `0px 0px 0px 8px ${color}70`,
    },
    '&.Mui-active': {
      boxShadow: `0px 0px 0px 12px ${color}70`,
    },
    '&:before': {
      boxShadow: 'none',
    },
  },
});

const Slider = (props: ISliderProps) => {
  const {
    sx,
    value,
    disabled = false,
    onChange,
    color = AB3,
    minDistance = 0.5,
    maxStepWithoutMargin = 1,
    ...rest
  } = props;

  const isValueAsArray = Array.isArray(value);

  const withMargin = useMemo(() => {
    if (!isValueAsArray) return false;
    const [firstValue, secondValue] = value;
    return Math.abs(secondValue - firstValue) <= maxStepWithoutMargin;
  }, [isValueAsArray, maxStepWithoutMargin, value]);

  const handleChange = useCallback((
    event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!onChange) return;
    if (!isValueAsArray || !Array.isArray(newValue)) {
      onChange(event, newValue, activeThumb);
      return;
    }
    const [prevFirstValue, prevSecondValue] = value;
    const [newFirstValue, newSecondValue] = newValue;
    if (activeThumb === 0) {
      onChange(
        event,
        [Math.min(newFirstValue, prevSecondValue - minDistance), prevSecondValue],
        activeThumb,
      );
      return;
    }
    onChange(
      event,
      [prevFirstValue, Math.max(newSecondValue, prevFirstValue + minDistance)],
      activeThumb,
    );
  }, [isValueAsArray, onChange, minDistance, value]);

  return (
    <MUISlider
      sx={mergeSX(buildSX({ color, withMargin, disabled }), sx)}
      disabled={disabled}
      onChange={handleChange}
      value={value}
      {...rest}
    />
  );
};

export default memo(Slider);
